import React, { useEffect, useState, useRef } from 'react';

interface LoadingScreenProps {
    isDarkMode?: boolean;
    onReady?: () => void;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ isDarkMode = true, onReady }) => {
    const [progress, setProgress] = useState(0);
    const [loadingText, setLoadingText] = useState('Initializing');
    const [phase, setPhase] = useState<'loading' | 'ready' | 'fadeout'>('loading');
    const [textFade, setTextFade] = useState(true);
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    useEffect(() => {
        const texts = [
            'Initializing',
            'Loading AI models',
            'Preparing your workspace',
            'Almost ready'
        ];
        
        let textIndex = 0;
        const textInterval = setInterval(() => {
            setTextFade(false);
            setTimeout(() => {
                textIndex = (textIndex + 1) % texts.length;
                setLoadingText(texts[textIndex]);
                setTextFade(true);
            }, 150);
        }, 800);

        // Smooth eased progress
        let frame: number;
        let start = Date.now();
        const duration = 1100;
        const animate = () => {
            const elapsed = Date.now() - start;
            const t = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
            setProgress(Math.round(eased * 95));
            if (t < 1) frame = requestAnimationFrame(animate);
        };
        frame = requestAnimationFrame(animate);

        // Ready transition
        const checkReady = setTimeout(() => {
            setLoadingText('Ready');
            setPhase('ready');
            setProgress(100);
            
            setTimeout(() => {
                setPhase('fadeout');
                setTimeout(() => {
                    onReadyRef.current?.();
                }, 350);
            }, 250);
        }, 1200);

        return () => {
            clearInterval(textInterval);
            clearTimeout(checkReady);
            cancelAnimationFrame(frame);
        };
    }, []);

    return (
        <div className={`fixed inset-0 z-[99999] flex flex-col items-center justify-center overflow-hidden ${
            isDarkMode 
                ? 'bg-[#0c0f1a]' 
                : 'bg-slate-50'
        }`} style={{ 
            position: 'fixed', 
            inset: 0,
            height: '100dvh',
            width: '100vw',
            margin: 0,
            padding: 0,
            overflow: 'hidden',
            opacity: phase === 'fadeout' ? 0 : 1,
            transition: 'opacity 0.35s ease-out',
        }}>
            {/* Ambient Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className={`absolute top-[20%] left-[15%] w-[400px] h-[400px] rounded-full blur-3xl ${
                    isDarkMode ? 'bg-indigo-600/[0.12]' : 'bg-indigo-400/[0.15]'
                }`} style={{ animation: 'float 8s ease-in-out infinite' }} />
                <div className={`absolute bottom-[15%] right-[10%] w-[350px] h-[350px] rounded-full blur-3xl ${
                    isDarkMode ? 'bg-amber-500/[0.08]' : 'bg-amber-400/[0.12]'
                }`} style={{ animation: 'float 8s ease-in-out infinite 3s' }} />
            </div>

            {/* Content */}
            <div className="relative z-10 flex flex-col items-center">
                {/* Logo */}
                <div className="relative mb-8" style={{ animation: 'fade-in-scale 0.5s ease-out both' }}>
                    <div className={`absolute -inset-3 rounded-full blur-2xl ${
                        isDarkMode ? 'bg-indigo-500/25' : 'bg-indigo-400/20'
                    }`} style={{ animation: 'pulse 3s ease-in-out infinite' }} />
                    
                    <div className={`relative w-[88px] h-[88px] rounded-[22px] overflow-hidden shadow-2xl ${
                        isDarkMode 
                            ? 'ring-1 ring-white/[0.08]' 
                            : 'ring-1 ring-slate-200'
                    }`}>
                        <img 
                            src="/robot-luca.png" 
                            alt="Luca" 
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.src = '/app-icon.png'; }}
                        />
                    </div>
                </div>

                {/* Brand Text */}
                <div className="text-center mb-10" style={{ animation: 'fade-in-up 0.5s ease-out 0.12s both' }}>
                    <h1 className={`text-[1.625rem] font-heading font-extrabold tracking-tight mb-1 ${
                        isDarkMode ? 'text-slate-100' : 'text-slate-900'
                    }`}>
                        Luca
                    </h1>
                    <p className={`text-sm font-medium ${
                        isDarkMode ? 'text-slate-500' : 'text-slate-500'
                    }`}>
                        Your Personal CPA Reviewer
                    </p>
                </div>

                {/* Loading Indicator */}
                <div className="flex flex-col items-center gap-4" style={{ animation: 'fade-in-up 0.5s ease-out 0.25s both' }}>
                    {/* Progress Bar */}
                    <div className="w-44">
                        <div className={`h-[3px] rounded-full overflow-hidden ${
                            isDarkMode ? 'bg-white/[0.06]' : 'bg-slate-200'
                        }`}>
                            <div 
                                className={`h-full rounded-full transition-[width] duration-300 ease-out ${
                                    isDarkMode 
                                        ? 'bg-gradient-to-r from-indigo-500 to-indigo-400' 
                                        : 'bg-gradient-to-r from-indigo-500 to-blue-500'
                                }`}
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        
                        {/* Loading Text */}
                        <p className={`text-center text-xs mt-3 font-medium ${
                            isDarkMode ? 'text-slate-600' : 'text-slate-400'
                        }`} style={{
                            opacity: textFade ? 1 : 0,
                            transition: 'opacity 0.15s ease',
                        }}>
                            {loadingText}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoadingScreen;
